/**
 * AuthService — cliente frontend para el backend FastAPI.
 *
 * Responsabilidades:
 *   - Guardar/leer el JWT en localStorage
 *   - Exponer user actual (cargado al boot si hay token)
 *   - fetch() con Authorization automático
 *   - Pub/sub mínimo para que componentes reaccionen al login/logout
 *
 * NO maneja UI. Eso lo hace LoginScreen.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'cgt_token';
const USER_KEY = 'cgt_user';

export type Role = 'student' | 'teacher';

export interface PublicUser {
  id: number;
  email: string;
  display_name: string;
  role: Role;
  teacher_id?: number | null;
  /** Room para sesión live (Sprint 5 multi-clase). Backend lo deriva. */
  live_room?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  user: PublicUser;
}

type Listener = (user: PublicUser | null) => void;

class AuthServiceImpl {
  private token: string | null = null;
  private user: PublicUser | null = null;
  private listeners = new Set<Listener>();

  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(USER_KEY);
    if (raw) {
      try {
        this.user = JSON.parse(raw) as PublicUser;
      } catch {
        this.user = null;
      }
    }
  }

  get currentUser(): PublicUser | null {
    return this.user;
  }

  get isAuthenticated(): boolean {
    return this.token !== null && this.user !== null;
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  async register(email: string, password: string, displayName: string, role: Role = 'student'): Promise<PublicUser> {
    const res = await this.request<TokenResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, display_name: displayName, role }),
    });
    this.persist(res);
    return res.user;
  }

  async login(email: string, password: string): Promise<PublicUser> {
    const res = await this.request<TokenResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.persist(res);
    return res.user;
  }

  logout(): void {
    this.token = null;
    this.user = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.notify();
  }

  /**
   * Refresca el perfil contra /auth/me. Necesario cuando agregamos campos
   * nuevos al esquema de UserPublic (ej. live_room, teacher_id) y el token
   * cacheado del usuario sigue siendo válido pero su `cgt_user` está
   * desactualizado. También útil para captar reasignaciones de profesor.
   */
  async refreshMe(): Promise<PublicUser | null> {
    if (!this.token) return null;
    try {
      const fresh = await this.request<PublicUser>('/auth/me');
      this.user = fresh;
      localStorage.setItem(USER_KEY, JSON.stringify(fresh));
      this.notify();
      return fresh;
    } catch {
      return null;
    }
  }

  /**
   * Wrapper sobre fetch: agrega Authorization, base URL y parseo JSON.
   * Usar para cualquier llamada autenticada al backend.
   */
  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers ?? {});
    headers.set('Content-Type', 'application/json');
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);

    const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
    if (res.status === 401) {
      this.logout();
      throw new Error('Sesión expirada. Volvé a iniciar sesión.');
    }
    if (!res.ok) {
      const body = await res.text();
      throw new Error(this.extractError(body) ?? `HTTP ${res.status}`);
    }
    // Algunos endpoints (/auth/logout futuro) pueden devolver 204.
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }

  private extractError(body: string): string | null {
    try {
      const parsed = JSON.parse(body);
      return typeof parsed.detail === 'string' ? parsed.detail : null;
    } catch {
      return null;
    }
  }

  private persist(response: TokenResponse): void {
    this.token = response.access_token;
    this.user = response.user;
    localStorage.setItem(TOKEN_KEY, response.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach(fn => fn(this.user));
  }
}

export const authService = new AuthServiceImpl();
